import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { Icon } from '@/design-system/components/icons/Icon';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { CurrenciesIcon } from '@/features/earn/components';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { SxProp, View } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { UnionType } from '@/features/bill-payment/enum';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { FundSourceSelectFilters, RemittancePurposeSelectFilters } from '@/features/bill-payment/screens/union-pay/options';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { usePayout } from '@/features/account-management/screens/details/hooks/usePayouts.hook';
import { CreatePayoutBodyDestination, CreatePayoutBodyPurpose, CreatePayoutBodySourceOfFunds } from '@utx/utx-access-client';
import { isEmpty, isString } from 'lodash';
import { errorParser } from '../otp/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface ConfirmationWebScreenProps {
  onLayout: (event: any) => void;
  onBack?: (flag?: boolean) => void;
  wallet?: any;
  calcFee?: any;
  beneficiaryInformation?: any;
  onChangeView?: () => void;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const ConfirmationWebScreen: React.FC<ConfirmationWebScreenProps> = (props) => {
  const {
    onLayout,
    onBack,
    wallet,
    calcFee,
    beneficiaryInformation,
    onChangeView,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();

  const layout = useContext(LayoutContext);

  const [viewShow, setViewShow] = useState(false);
  const onChangeViewLocal = () => {
    setViewShow(!viewShow);
  };

  const onCancel = () => {
    onBack && onBack();
    onChangeView && onChangeView();
  };

  const { runPayoutSubmit } = usePayout();
  const postPayoutSubmit = (
    destination: CreatePayoutBodyDestination,
    quoteId: string,
    purpose: CreatePayoutBodyPurpose,
    sourceOfFunds: CreatePayoutBodySourceOfFunds,
    accountId: string,
    remarks: string,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runPayoutSubmit(
      {
        method: 'UNIONPAY',
        destination: destination,
        quoteId: quoteId,
        purpose: purpose,
        sourceOfFunds: sourceOfFunds,
        accountId: accountId,
        remarks: remarks
      },
      onSuccess,
      onError
    );
  };

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState('');
  const submitPress = () => {
    if (unionType == UnionType.PAYMENT) {
      navigationAny.navigate(navigationNavigate, {
        screen: navigationScreen,
        params: {
          screen: UnionPayStackRoutes.TWOFA,
          onLayout,
          onBack,
          wallet,
          calcFee,
          beneficiaryInformation,
          onChangeView: onChangeViewLocal,
          unionType,
          refresh
        }
      });
    } else if (unionType == UnionType.ACCOUNT) {
      setIsLoadingSubmit(true);
      postPayoutSubmit(
        {
          accountNumber: beneficiaryInformation?.account?.accountNumber,
          firstName: beneficiaryInformation?.account?.firstNameEnglish,
          firstNameCN: beneficiaryInformation?.account?.firstNameChinese,
          surname: beneficiaryInformation?.account?.lastNameEnglish,
          surnameCN: beneficiaryInformation?.account?.lastNameChinese,
          address: beneficiaryInformation?.account?.accountHolderAddress
        },
        calcFee?.id,
        beneficiaryInformation?.purpose,
        beneficiaryInformation?.fundSource,
        wallet?.id,
        beneficiaryInformation?.remark,
        (data: any) => {
          setIsLoadingSubmit(false);
          let refId = data?.data?.refId;
          navigationAny.navigate(navigationNavigate, {
            screen: navigationScreen,
            params: { screen: UnionPayStackRoutes.Placed, onBack, wallet, calcFee, beneficiaryInformation, refId, unionType, refresh }
          });
        },
        (error: any) => {
          setIsLoadingSubmit(false);
          const data: any = error.response?.data;
          const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : error.message;
          setAlertMessage(errorMessage);
        }
      );
    }
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={sxStyles.modalHeader}>
        <ModalHeader
          title={t('unionPay::payment-instruction.title--payment-instruction-confirmation')}
          closeModal={() => {
            onBack && onBack(true);
          }}
        />
      </View>
      <View sx={sxStyles.body}>
        <View>
          <TextBody semiBold>{t('unionPay::payment-instruction.text--confirm-payment-instruction')}</TextBody>
          <View sx={{ gap: 8, my: '$5' }}>
            <View sx={sxStyles.sendBox}>
              <TextCaption semiBold>{t('unionPay::payment-instruction.text--you-send')}</TextCaption>
              <View sx={sxStyles.amount}>
                <CurrenciesIcon asset={calcFee?.feeCurrency} hideText={true} />
                <TextBody semiBold>
                  {formatAmountByDecimal({
                    value: (unionType == UnionType.PAYMENT ? calcFee?.attributes?.amount : calcFee?.sourceAmount) || 0,
                    suffix: calcFee?.feeCurrency,
                    decimals: 2
                  })}
                </TextBody>
              </View>
            </View>
            <View sx={sxStyles.sendBox}>
              <TextCaption semiBold>{t('unionPay::payment-instruction.text--recipient-receives')}</TextCaption>
              <View sx={sxStyles.amount}>
                <Icon name="rmb" width={28} height={28} />
                <TextBody semiBold>
                  {formatAmountByDecimal({
                    value: (unionType == UnionType.PAYMENT ? calcFee?.attributes?.destination?.amount : calcFee?.destinationAmount) || 0,
                    suffix: 'RMB',
                    decimals: 2
                  })}
                </TextBody>
              </View>
            </View>
          </View>
          <View sx={{ gap: 12 }}>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.glossary.conversion-rate')}</TextBody>
              <TextBody>
                {formatAmountByDecimal({
                  value: 1 / calcFee?.attributes?.destination?.rate,
                  prefix: '1 RMB =',
                  suffix: calcFee?.feeCurrency,
                  decimals: 4
                })}
              </TextBody>
            </TextBody>
            <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--from-account')}</TextBody>
              <View sx={{ textAlign: 'right' }}>
                <TextBody>
                  {wallet && (wallet?.accountLabel ? t(`earn-invest::glossary.${wallet?.accountLabel}`) : wallet?.accountName || ' - ')}
                </TextBody>
                <TextBody>{wallet && wallet?.accountNumber}</TextBody>
              </View>
            </TextBody>
            <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--to-account')}</TextBody>
              <View sx={{ textAlign: 'right' }}>
                <TextBody>{beneficiaryInformation?.account?.accountLabel}</TextBody>
                <TextBody>{beneficiaryInformation?.account?.accountNumber}</TextBody>
              </View>
            </TextBody>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.glossary.debit-amount')}</TextBody>
              <TextBody sx={{ color: '$borderInputError' }}>
                {formatAmountByDecimal({
                  value: calcFee?.instructionTotal,
                  suffix: calcFee?.feeCurrency,
                  decimals: 2
                })}
              </TextBody>
            </TextBody>
            {/* <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>Instruction Fee</TextBody>
              <TextBody sx={{ color: '$borderInputError' }}>20.00 HKD</TextBody>
            </TextBody> */}
            <Divider sx={{ my: '$2' }} />
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--purpose')}</TextBody>
              <TextBody>{RemittancePurposeSelectFilters(t).find((f) => f.value === beneficiaryInformation?.purpose)?.label}</TextBody>
            </TextBody>
            <TextBody sx={sxStyles.itemStyle}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--fund-source')}</TextBody>
              <TextBody>{FundSourceSelectFilters(t).find((f) => f.value === beneficiaryInformation?.fundSource)?.label}</TextBody>
            </TextBody>
            <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
              <TextBody secondary>{t('unionPay::payment-instruction.text--remarks')}</TextBody>
              <TextBody>{beneficiaryInformation?.remark || '-'}</TextBody>
            </TextBody>
          </View>
        </View>
        {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
        {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={{ ...sxStyles.buttonBox, flexDirection: 'row', gap: '$2' }}>
          <UniButton
            wrapperSx={{ width: '48%' }}
            variant={ButtonVariant.outline1}
            labelSx={{ color: '$textPrimary' }}
            sx={{ width: '100%', height: 44 }}
            label={t('global-bill-payment::glossary.back')}
            onPress={onCancel}
          />
          <UniButton
            wrapperSx={{ width: '48%' }}
            sx={{ width: '100%', height: 44 }}
            labelSx={{ color: '$balck' }}
            label={t('unionPay::payment-instruction.button--place-instruction')}
            onPress={submitPress}
            loading={isLoadingSubmit}
            disabled={unionType == UnionType.ACCOUNT && isLoadingSubmit ? true : false}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flex: 1
  },
  scrollView: {
    padding: '$3'
  },
  body: {
    padding: '$7'
  },
  modalHeader: {
    height: 70,
    justifyContent: 'center'
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$7',
    backgroundColor: '$backgroundSecondary'
  },
  title: {
    flex: 142,
    pt: '$10',
    pb: '$5'
  },
  header: {
    flexDirection: 'column'
    // padding: '$4'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  scrollViewWrapper: {
    pt: '$4',
    pb: '$10',
    px: '$4'
  },
  viewAlert: {
    mt: '$5'
  },
  sendBox: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$4',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10
  },
  amount: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '$2'
  },
  itemStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 20
  },
  topCenter: {
    alignItems: 'flex-start'
  },
  buttonBox: {
    mt: '$7'
  }
};
